import { GENERATION_SOURCE_TYPE } from 'src/data/generation/const';
import { isDummyVariationId } from 'src/data/variation';

import type { FeedAssetGroup, LongFormFeedAsset, ShortFormFeedAsset } from './types';
import { isLocalizationAssetGroup, isLongFormAssetGroup } from './utils';

export const ASSET_GROUP_STATUS = {
  generating: 'generating',
  partial: 'partial',
  ready: 'ready',
  failed: 'failed',
} as const;

export const LONG_FORM_ASSET_TEXT_STATUS = {
  empty: 'empty',
  completed: 'completed',
  improved: 'improved',
  edited: 'edited',
  accepted: 'accepted',
  doneWithErrors: 'done_with_errors',
} as const;

/**
 * Whether a long-form asset is still waiting on the run that will fill it in.
 *
 * Two ways an asset can be waiting, and a long-form asset only ever arrives by the second:
 *
 * - a negative-id placeholder, which the backend's skeleton hydrator adds for an asset that does
 *   not exist yet;
 * - a real row whose group is still generating and whose own text has not landed. A long-form
 *   asset is created up front with a real id, so its id says nothing about whether it is ready.
 *
 * Both halves of the second case are needed. The group alone is too coarse — its assets complete
 * one at a time, so a generating group holds finished assets alongside waiting ones. The asset
 * alone is too broad — `empty` is also how an asset the user emptied by hand reads, and that one
 * is finished, not pending.
 *
 * This is the long-form editor's `section.status === 'generating'` after that field was split in
 * two by the move into short-form projects: the status went to the group, the text status to the
 * asset.
 */
export const isLongFormAssetGenerating = (
  asset: LongFormFeedAsset,
  group: Pick<FeedAssetGroup, 'status'>
): boolean => {
  if (isDummyVariationId(asset.id)) {
    return true;
  }

  return (
    group.status === ASSET_GROUP_STATUS.generating &&
    asset.textStatus === LONG_FORM_ASSET_TEXT_STATUS.empty
  );
};

/**
 * Whether an improve is still writing into this group. An improve's group arrives holding the text
 * it was given before it holds anything improved, and the run reports the improved parts one at a
 * time — so what the card shows in between depends on the run rather than on the assets, which are
 * finished rows either way.
 */
export const isImproveInProgress = (
  group: Pick<FeedAssetGroup, 'sourceType' | 'status'>
): boolean =>
  (group.sourceType === GENERATION_SOURCE_TYPE.contentImprover ||
    isLocalizationAssetGroup(group)) &&
  group.status === ASSET_GROUP_STATUS.generating;

/**
 * Whether the asset's score is still on its way. An asset is written before it is scored — a run
 * reports the text first and the score a poll or two later — so a scoreless asset in a group that
 * is still working is waiting for one, while the same asset in a settled group came back unscored
 * and will stay that way.
 */
export const isLongFormAssetAwaitingScore = (
  asset: Pick<LongFormFeedAsset, 'score'>,
  group: Pick<FeedAssetGroup, 'status'>
): boolean => asset.score == null && group.status === ASSET_GROUP_STATUS.generating;

/**
 * Whether a short-form asset is still waiting on the run that will fill it in.
 *
 * A negative-id placeholder is the whole question here — the backend's skeleton hydrator adds one
 * for an asset that does not exist yet, and a short-form asset is written only once it has been
 * generated, so nothing else about it can be half-finished. Its long-form counterpart has to ask
 * more, being created up front with a real id.
 */
export const isShortFormAssetGenerating = (asset: Pick<ShortFormFeedAsset, 'id'>): boolean =>
  isDummyVariationId(asset.id);

/**
 * The group with everything the run never filled in dropped: the assets waiting on it, and the
 * `generating` status itself. For a run that ended badly — polling stops at the failure, so what
 * it had written by then is as far as it will ever get.
 *
 * Only a group the backend still reports as `generating` is touched. One run can land several
 * groups, and a card that had already settled as `ready` before a *different* card in the same run
 * failed has nothing to discard — rewriting its status to `partial` would take back a claim the
 * backend already confirmed.
 *
 * The status matters beyond the assets on screen. A section added to the card afterwards starts
 * with empty text, which reads as generating for as long as its group claims to be running, so a
 * card left describing itself that way would show every later section as a skeleton too.
 */
export const withoutUnfinishedAssets = (group: FeedAssetGroup): FeedAssetGroup => {
  if (group.status !== ASSET_GROUP_STATUS.generating) {
    return group;
  }

  return {
    ...group,
    assets: isLongFormAssetGroup(group)
      ? group.assets.filter((asset) => !isLongFormAssetGenerating(asset, group))
      : group.assets.filter((asset) => !isShortFormAssetGenerating(asset)),
    status: ASSET_GROUP_STATUS.partial,
  };
};
